import React from 'react';
import { useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSelector } from './store/store';
import { useDispatch } from 'react-redux';
import { setUser as setUserStore } from './store/loginUserSlice';
import { Elements } from '@stripe/react-stripe-js';
import Login from './components/pages/Login';
import Incomings from './components/pages/Incomings';
import Dashboard from './components/pages/Dashboard';
import Terms from './components/pages/Terms';
import IncomingSetting from './components/pages/IncomingSetting';
import Payments from './components/pages/Payments';
import Account from './components/pages/Account';
import Setup from './components/pages/Setup';
import Lecture from './components/pages/Lecture';
import RetryPayment from './components/pages/RetryPayment';
import RetryPaymentDemo from './components/pages/RetryPaymentDemo';
import ProtectedRoute from './components/common/ProtectedRoute';
import { loadStripe } from '@stripe/stripe-js';
import { AnalyticsBrowser } from '@segment/analytics-next';

const mdTheme = createTheme();

// Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Segment
const AnalyticsContext = React.createContext<AnalyticsBrowser>(undefined!);
const { REACT_APP_SEGMENT_WRITE_KEY } = process.env;

type Props = {
  writeKey: string;
  children: React.ReactNode;
};
export const AnalyticsProvider = ({ children, writeKey }: Props) => {
  const analytics = React.useMemo(
    () => AnalyticsBrowser.load({ writeKey }),
    [writeKey],
  );
  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const result = React.useContext(AnalyticsContext);
  if (!result) {
    throw new Error('Context used outside of its Provider!');
  }
  return result;
};

const App: React.FC = () => {
  console.log(`🐸 App.tsx`);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.loginUser.user);
  const sessionExpired = useSelector((state) => state.loginUser.sessionExpired);
  console.log(`🐞 sessionExpired: ${sessionExpired} ${user?.id}`);

  useMemo(() => {
    console.log(`🐞 useMemo`);
    const now = new Date();
    if (sessionExpired < now.getTime()) {
      // セッションが切れている
      console.log(`🐞 Session expired.`);
      dispatch(setUserStore(null));
    }
  }, [sessionExpired, dispatch]);

  return (
    <AnalyticsProvider writeKey={REACT_APP_SEGMENT_WRITE_KEY}>
      <ThemeProvider theme={mdTheme}>
        <Router>
          <Routes>
            <Route path='/login' element={<Login />} />
            <Route path='/terms' element={<Terms />} />
            <Route
              path='/setup'
              element={
                <Elements stripe={stripePromise}>
                  <Setup />
                </Elements>
              }
            />
            <Route path='/lecture' element={<Lecture />} />
            <Route
              path='/incomings'
              element={
                <ProtectedRoute isAuthenticated={!!user?.id}>
                  <Incomings />
                </ProtectedRoute>
              }
            />
            <Route
              path='/incoming-setting'
              element={
                <ProtectedRoute isAuthenticated={!!user?.id}>
                  <IncomingSetting />
                </ProtectedRoute>
              }
            />
            <Route
              path='/payments'
              element={
                <ProtectedRoute isAuthenticated={!!user?.id}>
                  <Elements stripe={stripePromise}>
                    <Payments />
                  </Elements>
                </ProtectedRoute>
              }
            />
            <Route
              path='/account'
              element={
                <ProtectedRoute isAuthenticated={!!user?.id}>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path='/retry-payment/:paymentIntentId'
              element={
                <ProtectedRoute isAuthenticated={!!user?.id}>
                  <Elements stripe={stripePromise}>
                    <RetryPayment />
                  </Elements>
                </ProtectedRoute>
              }
            />
            <Route
              path='/retry-payment-demo'
              element={<RetryPaymentDemo />}
            />
            {/* ワイルドカードルートは最後に配置 */}
            <Route path='*' element={!user?.id ? <Login /> : <Dashboard />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </AnalyticsProvider>
  );
};
export default App;
